import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, Divider, CardHeader } from "@heroui/react";
import { Icon } from "@iconify/react";
import { apiClientExporter } from "../../services/apiClient";
import generateClaimLetter from "../../components/claim/CartaReclamoHelper";
import { saveAs } from "file-saver";
import { Car } from "lucide-react";
import { toast } from "sonner";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Debe coincidir con la versión de pdfjs-dist que usa react-pdf (no el pdf.worker en public/, viejo).
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ClaimLetterTab = ({ maestraData, readOnlyClaims = false }) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [error, setError] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1.0);


    // useEffect(() => {
    //     console.log("maestraData:", maestraData);
    // }, [maestraData]);

    const handleGenerateClaimLetter = async () => {
        if (readOnlyClaims) return;
        if (!maestraData?.rsk_ref) {
            setError("No RSK reference available");
            return;
        }
        
        if (!maestraData?.reclamo_usd || maestraData?.reclamo_usd === 0) {
            setError("No claim amount available. Claim letter cannot be generated without a valid claim amount.");
            return;
        }

        setIsGenerating(true);
        setError(null);

        try {
            // Obtener datos del reclamo
            const response = await apiClientExporter.get(`/exporter/reclamo?rsk_ref=${maestraData.rsk_ref}`);
            const reclamoData = response.data;
            const reclamo = Array.isArray(reclamoData) ? reclamoData[0] : reclamoData;

            // Generar el documento DOCX
            const { blob } = await generateClaimLetter(maestraData.rsk_ref, reclamo, false);
            
            // Convertir DOCX a PDF para previsualización
            const pdfResponse = await apiClientExporter.post('/exporter/convert-to-pdf', blob, {
                headers: {
                    'Content-Type': 'application/octet-stream',
                },
                responseType: 'blob',
            });

            const pdfBlob = new Blob([pdfResponse.data], { type: 'application/pdf' });
            
            // Crear URL para previsualización del PDF
            const url = URL.createObjectURL(pdfBlob);
            setPreviewBlob(blob); // Guardar el DOCX original para descarga
            setPdfBlob(pdfBlob); // Guardar el PDF para descarga
            setPreviewUrl(url); // URL del PDF para previsualización
            
            // Mostrar toast de éxito
            toast.success("Claim Letter Generated Successfully", {
                duration: 3000,
                position: "top-right",
            });
            
        } catch (error) {
            console.error("Error generating claim letter:", error);
            setError("Error generating claim letter. Please try again.");
            toast.error("Error generating claim letter. Please try again.", {
                duration: 4000,
                position: "top-right",
            });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDownloadWord = () => {
        if (previewBlob) {
            saveAs(previewBlob, `Claim_Letter_${maestraData.rsk_ref}.docx`);
            toast.success("Word Document Downloaded Successfully", {
                duration: 3000,
                position: "top-right",
            });
        }
    };

    const handleDownloadPdf = () => {
        if (pdfBlob) {
            saveAs(pdfBlob, `Claim_Letter_${maestraData.rsk_ref}.pdf`);
            toast.success("PDF Document Downloaded Successfully", {
                duration: 3000,
                position: "top-right",
            });
        }
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPageNumber(1);
    };

    const onDocumentLoadError = (error) => {
        console.error('Error loading PDF:', error);
        setError('Error loading PDF document');
    };

    const changePage = (offset) => {
        setPageNumber(prevPageNumber => {
            const newPageNumber = prevPageNumber + offset;
            return Math.max(1, Math.min(newPageNumber, numPages));
        });
    };

    const changeScale = (newScale) => {
        setScale(Math.max(0.5, Math.min(2.0, newScale)));
    };


    // Limpiar URL cuando el componente se desmonte
    React.useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    return (
        <Card className="h-full flex flex-col">
            <CardHeader className="flex-shrink-0">
                <div className="flex justify-between items-center w-full">
                    <div className="flex flex-col gap-1 items-start justify-center">
                        <h4 className="text-small font-semibold leading-none text-default-600">Claim Letter Generator</h4>
                        <h5 className="text-small tracking-tight text-default-400">Generate and download claim letters</h5>
                    </div>

                    <div className="flex gap-2">
                        <Button
                            color="primary"
                            radius="full"
                            size="sm"
                            onPress={handleGenerateClaimLetter}
                            isLoading={isGenerating}
                            isDisabled={readOnlyClaims || isGenerating}
                            startContent={!isGenerating && <Icon icon="lucide:file-plus" />}
                        >
                            {isGenerating ? "Generating..." : "Generate & Preview"}
                        </Button>
                        
                        {previewBlob && (
                            <>
                                <Button
                                    color="success"
                                    radius="full"
                                    size="sm"
                                    onPress={handleDownloadWord}
                                    startContent={<Icon icon="lucide:file-text" />}
                                >
                                    Download Word
                                </Button>
                                <Button
                                    color="primary"
                                    radius="full"
                                    size="sm"
                                    onPress={handleDownloadPdf}
                                    startContent={<Icon icon="lucide:file-type" />}
                                >
                                    Download PDF
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardBody className="flex-1 flex flex-col min-h-0">
                {!previewBlob ? (
                    <div className="p-4 flex justify-center items-center h-full">
                        {error ? (
                            <div className="text-center">
                                <Icon icon="lucide:alert-circle" className="text-6xl text-red-400 mb-4 mx-auto" />
                                <h2 className="text-lg font-semibold mb-2 text-red-600">Error generating claim letter</h2>
                                <p className="text-red-500 mb-4">{error}</p>
                                <Button
                                    color="primary"
                                    onPress={handleGenerateClaimLetter}
                                    isLoading={isGenerating}
                                    isDisabled={readOnlyClaims || isGenerating}
                                >
                                    Try Again
                                </Button>
                            </div>
                        ) : (
                            <h2 className="text-lg font-semibold mb-2 text-orange-600">Claim Letter not yet generated, please click on the button above to generate it</h2>
                        )}
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col min-h-0">
                        
                        <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col min-h-0">
                            <h5 className="text-lg font-semibold mb-4 flex items-center gap-2 flex-shrink-0">
                                <Icon icon="lucide:eye" />
                                Preview
                            </h5>
                            <div className="flex-1 bg-white border border-gray-300 rounded-lg overflow-hidden min-h-0 flex flex-col" style={{ minHeight: "600px" }}>
                                {previewUrl ? (
                                    <>
                                        {/* Controles del PDF */}
                                        <div className="flex items-center justify-between p-3 bg-gray-50 border-b">
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    onPress={() => changePage(-1)}
                                                    isDisabled={pageNumber <= 1}
                                                    startContent={<Icon icon="lucide:chevron-left" />}
                                                >
                                                    Previous
                                                </Button>
                                                <span className="text-sm text-gray-600">
                                                    Page {pageNumber} of {numPages || '--'}
                                                </span>
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    onPress={() => changePage(1)}
                                                    isDisabled={pageNumber >= numPages}
                                                    endContent={<Icon icon="lucide:chevron-right" />}
                                                >
                                                    Next
                                                </Button>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    onPress={() => changeScale(scale - 0.1)}
                                                    startContent={<Icon icon="lucide:zoom-out" />}
                                                >
                                                    Zoom Out
                                                </Button>
                                                <span className="text-sm text-gray-600 min-w-16 text-center">
                                                    {Math.round(scale * 100)}%
                                                </span>
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    onPress={() => changeScale(scale + 0.1)}
                                                    endContent={<Icon icon="lucide:zoom-in" />}
                                                >
                                                    Zoom In
                                                </Button>
                                            </div>
                                        </div>
                                        
                                        {/* Visor del PDF */}
                                        <div className="flex-1 flex justify-center items-start p-4 overflow-auto">
                                            <Document
                                                file={previewUrl}
                                                onLoadSuccess={onDocumentLoadSuccess}
                                                onLoadError={onDocumentLoadError}
                                                loading={
                                                    <div className="flex items-center justify-center h-64">
                                                        <div className="text-center">
                                                            <Icon icon="lucide:loader-2" className="text-4xl text-gray-400 mb-2 animate-spin" />
                                                            <p className="text-gray-600">Loading PDF...</p>
                                                        </div>
                                                    </div>
                                                }
                                                error={
                                                    <div className="flex items-center justify-center h-64">
                                                        <div className="text-center">
                                                            <Icon icon="lucide:alert-circle" className="text-4xl text-red-400 mb-2" />
                                                            <p className="text-red-600">Error loading PDF</p>
                                                        </div>
                                                    </div>
                                                }
                                            >
                                                <Page
                                                    pageNumber={pageNumber}
                                                    scale={scale}
                                                    className="shadow-lg"
                                                />
                                            </Document>
                                        </div>
                                    </>
                                ) : (
                                    <div className="h-full flex items-center justify-center">
                                        <div className="text-center">
                                            <Icon icon="lucide:file-text" className="text-4xl text-gray-400 mb-2" />
                                            <p className="text-gray-600 mb-4">Claim Letter Document</p>
                                            <p className="text-sm text-gray-500">
                                                {maestraData.rsk_ref} - {maestraData.embarcador}
                                            </p>
                                            <p className="text-sm text-gray-500 mt-2">
                                                Document generated successfully. Click "Download" to save the file.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default ClaimLetterTab;
